import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Download, TrendingDown, TrendingUp, Scissors, Receipt, CheckCircle2, Banknote } from 'lucide-react';
import { useStats } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScopeSwitcher } from '@/components/ScopeSwitcher';
import { dateISO, fmtMoney, PAYMENT_LABEL } from '@/lib/format';
import { exportStatsToPDF } from '@/lib/pdf';
import { downloadCsv } from '@/lib/csv';

type Period = 'today' | 'week' | 'month' | 'year' | 'custom';

const computeRange = (p: Period, from?: string, to?: string) => {
  const now = new Date();
  if (p === 'today') return { from: dateISO(now), to: dateISO(now) };
  if (p === 'week') {
    const start = new Date(now);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    return { from: dateISO(start), to: dateISO(now) };
  }
  if (p === 'month') {
    const s = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: dateISO(s), to: dateISO(now) };
  }
  if (p === 'year') {
    const s = new Date(now.getFullYear(), 0, 1);
    return { from: dateISO(s), to: dateISO(now) };
  }
  return { from: from ?? dateISO(now), to: to ?? dateISO(now) };
};

export const StatsPage = () => {
  const [period, setPeriod] = useState<Period>('month');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const range = useMemo(() => computeRange(period, customFrom, customTo), [period, customFrom, customTo]);
  const { data: stats, isLoading, isError, error, refetch } = useStats(range);

  if (isError) {
    return (
      <div className="space-y-3 pb-2">
        <h1 className="text-2xl font-bold tracking-tight">Статистика</h1>
        <Card>
          <CardContent className="space-y-3 p-4 text-sm">
            <div className="font-medium text-destructive">
              {(error as any)?.response?.data?.error ?? 'Не удалось загрузить статистику'}
            </div>
            <Button variant="outline" onClick={() => refetch()}>
              Повторить
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading || !stats) {
    return (
      <div className="space-y-4 pb-2">
        <div className="skeleton h-8 w-32" />
        <div className="skeleton h-11 w-full" />
        <div className="grid grid-cols-2 gap-2.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-24" />
          ))}
        </div>
        <div className="skeleton h-64 w-full" />
      </div>
    );
  }

  const isProfit = stats.finance.profit >= 0;

  return (
    <div className="animate-in-up space-y-4 pb-2">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Статистика</h1>
        <ScopeSwitcher />
      </div>

      <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
        <TabsList>
          <TabsTrigger value="today">День</TabsTrigger>
          <TabsTrigger value="week">Неделя</TabsTrigger>
          <TabsTrigger value="month">Месяц</TabsTrigger>
          <TabsTrigger value="year">Год</TabsTrigger>
          <TabsTrigger value="custom">…</TabsTrigger>
        </TabsList>
      </Tabs>

      {period === 'custom' && (
        <div className="flex gap-2">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="h-11 flex-1 rounded-xl border border-input bg-card px-4 text-base shadow-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15"
          />
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="h-11 flex-1 rounded-xl border border-input bg-card px-4 text-base shadow-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15"
          />
        </div>
      )}

      {/* Hero KPI: revenue (gradient) */}
      <div className="relative overflow-hidden rounded-3xl bg-brand p-5 text-white shadow-soft-lg">
        <div
          aria-hidden
          className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/10 blur-2xl"
        />
        <div
          aria-hidden
          className="absolute -bottom-14 -left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl"
        />
        <div className="relative">
          <div className="text-xs font-bold uppercase tracking-widest opacity-90">Выручка</div>
          <div className="mt-1 text-4xl font-bold tabular leading-tight">
            {fmtMoney(stats.finance.revenue_total)}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/85">
            <span>{stats.load.total} записей</span>
            {stats.finance.revenue_other > 0 && (
              <>
                <span className="opacity-60">·</span>
                <span>прочие {fmtMoney(stats.finance.revenue_other)}</span>
              </>
            )}
            <span className="opacity-60">·</span>
            <span>средний чек {fmtMoney(stats.finance.avg_check)}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <KpiCard
          icon={isProfit ? TrendingUp : TrendingDown}
          label="Прибыль"
          value={fmtMoney(stats.finance.profit)}
          tint={isProfit ? 'success' : 'destructive'}
        />
        <KpiCard
          icon={Scissors}
          label="Расходы (волосы)"
          value={fmtMoney(stats.finance.expenses_hair ?? 0)}
          tint="muted"
        />
        <KpiCard
          icon={Receipt}
          label="Прочие расходы"
          value={fmtMoney(stats.finance.expenses_other ?? 0)}
          tint="muted"
        />
        <KpiCard
          icon={Receipt}
          label="Расходы всего"
          value={fmtMoney(stats.finance.expenses_total)}
          tint="accent"
        />
        {stats.finance.debt > 0 ? (
          <KpiCard
            icon={TrendingDown}
            label="Долги"
            value={fmtMoney(stats.finance.debt)}
            tint="warning"
          />
        ) : (
          <KpiCard
            icon={CheckCircle2}
            label="Завершено"
            value={String(stats.load.total)}
            tint="primary"
          />
        )}
        <KpiCard
          icon={Banknote}
          label="Средний чек"
          value={fmtMoney(stats.finance.avg_check)}
          tint="accent"
        />
      </div>

      <ChartCard title="Выручка по дням">
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={stats.series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <defs>
              <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(d) => new Date(d).getDate().toString()}
              fontSize={11}
              stroke="hsl(var(--muted-foreground))"
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              fontSize={11}
              tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)}
              stroke="hsl(var(--muted-foreground))"
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              cursor={{ stroke: 'hsl(var(--primary))', strokeWidth: 1, strokeDasharray: '3 3' }}
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 12,
                fontSize: 12,
                boxShadow: '0 10px 30px -10px rgb(0 0 0 / 0.15)',
              }}
              formatter={(v: any) => [fmtMoney(v as number), 'Выручка']}
              labelFormatter={(d) => new Date(d as string).toLocaleDateString('ru-RU')}
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="hsl(var(--primary))"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 0 }}
              fill="url(#revGrad)"
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Услуги">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={stats.services.distribution}
              dataKey="revenue"
              nameKey="name"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={3}
              stroke="none"
            >
            {stats.services.distribution.map((s: any, i: number) => (
              <Cell key={s.id ?? `${s.name}-${i}`} fill={SERVICE_COLORS[i % SERVICE_COLORS.length]} />
            ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 12,
                fontSize: 12,
              }}
              formatter={(v: any) => fmtMoney(v as number)}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="mt-3 grid grid-cols-2 gap-1.5 text-xs">
          {stats.services.distribution.slice(0, 6).map((s: any, i: number) => (
            <div key={s.id ?? `${s.name}-${i}`} className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: SERVICE_COLORS[i % SERVICE_COLORS.length] }}
              />
              <span className="truncate font-medium">{s.name}</span>
              <span className="ml-auto tabular text-muted-foreground">
                {fmtMoney(s.revenue)}
              </span>
            </div>
          ))}
        </div>
      </ChartCard>

      <ChartCard title="Способы оплаты">
        <div className="space-y-2 text-sm">
          {Object.entries(stats.finance.payments_distribution).map(([k, v]) => {
            const total = Object.values(stats.finance.payments_distribution).reduce(
              (s: number, x: any) => s + Number(x),
              0,
            );
            const pct = total ? (Number(v) / total) * 100 : 0;
            return (
              <div key={k}>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-muted-foreground">{PAYMENT_LABEL[k] ?? k}</span>
                  <span className="font-bold tabular">{fmtMoney(v as number)}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-brand transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </ChartCard>

      <ChartCard title="Загрузка по дням и часам">
        <Heatmap data={stats.load.heatmap} />
      </ChartCard>

      <ChartCard title="Топ клиентов">
        <div className="space-y-2">
          {stats.clients.top.map((c: any, i: number) => (
            <div key={c.id} className="flex items-center gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-xs font-bold text-primary">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.count} визитов</div>
              </div>
              <div className="tabular text-sm font-bold">{fmtMoney(c.revenue)}</div>
            </div>
          ))}
          {!stats.clients.top.length && (
            <div className="py-2 text-center text-xs text-muted-foreground">Нет данных</div>
          )}
        </div>
      </ChartCard>

      <ChartCard title="Материалы">
        <div className="grid grid-cols-3 gap-2">
          <MaterialStat label="Использовано" value={`${stats.materials.hair_grams_used.toFixed(0)}г`} />
          <MaterialStat label="Прядей" value={String(stats.materials.total_strands)} />
          <MaterialStat label="На складе" value={`${stats.materials.stock_total_grams.toFixed(0)}г`} />
        </div>
      </ChartCard>

      <ChartCard title="Расходы по категориям">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart
            data={stats.finance.expenses_by_category}
            margin={{ top: 8, right: 0, left: -20, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
            <XAxis
              dataKey="name"
              fontSize={10}
              stroke="hsl(var(--muted-foreground))"
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              fontSize={10}
              tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)}
              stroke="hsl(var(--muted-foreground))"
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              cursor={{ fill: 'hsl(var(--muted))' }}
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 12,
                fontSize: 12,
              }}
              formatter={(v: any) => fmtMoney(v as number)}
            />
            <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => exportStatsToPDF(stats)}>
          <Download className="h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" onClick={() => exportStatsToCsv(stats, range)}>
          <Download className="h-4 w-4" /> CSV
        </Button>
      </div>
    </div>
  );
};

const SERVICE_COLORS = [
  'hsl(268 76% 58%)',
  'hsl(322 84% 58%)',
  'hsl(220 90% 60%)',
  'hsl(152 65% 48%)',
  'hsl(32 95% 55%)',
  'hsl(190 80% 50%)',
];

const ChartCard = ({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <Card>
    <CardContent className="pt-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-bold tracking-tight">{title}</h3>
        {action}
      </div>
      {children}
    </CardContent>
  </Card>
);

const MaterialStat = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl bg-secondary p-3 text-center">
    <div className="text-base font-bold tabular">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
  </div>
);

const exportStatsToCsv = (stats: any, range: { from: string; to: string }) => {
  const fname = `chernova-stats_${range.from}_${range.to}`;

  // Sheet 1: KPIs (single CSV with multiple sections separated by blank lines)
  const kpis = [
    ['KPI', 'Значение'],
    ['Период', `${range.from} — ${range.to}`],
    ['Выручка', stats.finance.revenue_total],
    ['  С записей', stats.finance.revenue_appointments],
    ['  Прочие доходы', stats.finance.revenue_other],
    ['Расходы (волосы)', stats.finance.expenses_hair ?? 0],
    ['Прочие расходы', stats.finance.expenses_other ?? 0],
    ['Расходы всего', stats.finance.expenses_total],
    ['Прибыль', stats.finance.profit],
    ['Средний чек', stats.finance.avg_check],
    ['Записей', stats.load.total],
    ['Долги', stats.finance.debt],
    [],
    ['По дням', 'Выручка', 'Записей'],
    ...stats.series.map((s: any) => [s.date, s.revenue, s.appointments ?? s.count]),
    [],
    ['Услуги', 'Выручка', 'Кол-во'],
    ...stats.services.distribution.map((s: any) => [s.name, s.revenue, s.count]),
    [],
    ['Способы оплаты', 'Сумма'],
    ...Object.entries(stats.finance.payments_distribution).map(([k, v]) => [PAYMENT_LABEL[k] ?? k, v as number]),
    [],
    ['Топ клиентов', 'Визитов', 'Выручка'],
    ...stats.clients.top.map((c: any) => [c.name, c.count, c.revenue]),
    [],
    ['Прочие доходы', 'Сумма'],
    ...(stats.finance.other_incomes ?? []).map((o: any) => [o.title, o.amount]),
    [],
    ['Расходы по категориям', 'Сумма'],
    ...stats.finance.expenses_by_category.map((c: any) => [c.name, c.amount]),
  ];
  const csv =
    '\uFEFF' +
    kpis
      .map((row) =>
        row
          .map((c: unknown) => {
            if (c === null || c === undefined) return '';
            const s = String(c);
            return /[",;\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(';'),
      )
      .join('\n');
  downloadCsv(fname, csv);
};

const KpiCard = ({
  label,
  value,
  icon: Icon,
  tint,
}: {
  label: string;
  value: string;
  icon?: any;
  tint?: 'primary' | 'success' | 'destructive' | 'warning' | 'accent' | 'muted';
}) => {
  const tints: Record<string, string> = {
    primary: 'text-primary bg-primary-soft',
    success: 'text-success bg-success-soft',
    destructive: 'text-destructive bg-destructive-soft',
    warning: 'text-warning bg-warning-soft',
    accent: 'text-accent bg-accent-soft',
    muted: 'text-muted-foreground bg-muted',
  };
  return (
    <Card className="h-full overflow-hidden">
      <CardContent className="flex h-full flex-col p-3">
        <span
          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
            Icon && tint ? tints[tint] : 'bg-transparent'
          }`}
        >
          {Icon ? <Icon className="h-4 w-4" /> : null}
        </span>
        <div className="mt-2 flex flex-1 flex-col justify-end">
          <div className="text-base font-bold tabular leading-tight tracking-tight">{value}</div>
          <div className="mt-1 text-[11px] font-medium uppercase leading-snug tracking-wider text-muted-foreground">
            {label}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Heatmap = ({ data }: { data: number[][] }) => {
  const max = Math.max(...data.flat(), 1);
  const days = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  return (
    <div className="overflow-x-auto">
      <div className="grid grid-cols-[auto_repeat(24,minmax(12px,1fr))] gap-1 text-[10px]">
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="text-center font-medium text-muted-foreground">
            {h % 3 === 0 ? h : ''}
          </div>
        ))}
        {data.map((row, r) => (
          <React.Fragment key={r}>
            <div className="pr-1 text-right font-bold text-muted-foreground">
              {days[r]}
            </div>
            {row.map((v, h) => (
              <div
                key={`${r}-${h}`}
                title={`${days[r]} ${h}:00 — ${v} зап.`}
                className="aspect-square rounded transition-transform hover:scale-110"
                style={{
                  background:
                    v === 0
                      ? 'hsl(var(--muted))'
                      : `hsl(var(--primary) / ${Math.max(0.18, v / max)})`,
                }}
              />
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
